import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { EditEvent, GridComponent, RemoveEvent } from '@progress/kendo-angular-grid';
import { Observable, of } from 'rxjs';

import { GridWrapper } from '../../../../_models/GridWrapper';
import { EtapeScenario } from '../../../../_models/EtapeScenario';
import { PlanGestionMobilite } from '../../../../_models/PlanGestionMobilite';
import { ConfigService } from '../../../../_services/local/config.service';
import { catchError, delay, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { SpService } from 'src/app/_services/http/sp.service';
import { BaseObject } from 'src/app/_models/BaseObject';

@Component({
  selector: 'app-tab-etapes',
  templateUrl: './tab-etapes.component.html',
  styleUrls: ['./tab-etapes.component.scss'],
})
export class TabEtapesComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() saving = false;
  @Input() isReadOnly = true;

  @ViewChild('kendoGrid', { static: true }) kendoGrid!: GridComponent;

  public readonly spTypes = this.configService.Config.spTypes;
  public pgms: PlanGestionMobilite[] = [];

  // Grid attributes
  public height = document.body.clientHeight * 0.8 - (48 + 48 + 75 + 16 + 40);
  public gridWrapper!: GridWrapper<EtapeScenario>;

  private inputEtapes!: EtapeScenario[];
  private originalEtapes!: EtapeScenario[];

  constructor(private fb: FormBuilder, private configService: ConfigService, private spService: SpService) {}

  ngOnInit(): void {
    console.debug('form', this.form.value);

    this.inputEtapes = this.form.get('etapes')?.value as EtapeScenario[] || [];
    this.originalEtapes = this.inputEtapes.map((x) => x.clone(true));

    this.gridWrapper = new GridWrapper<EtapeScenario>(
      'EtapeScenario',
      of(this.inputEtapes || []),
      {
        logic: 'or',
        filters: [
          {
            field: 'nom',
            operator: 'contains',
          },
        ],
      },
      this.kendoGrid,
      [],
      this.editHandler.bind(this)
    );
  }

  ngOnDestroy(): void {
    this.gridWrapper.destroy();
  }

  public moveDown(item: BaseObject) {
    this.gridWrapper.moveDown(item);
    this.form.markAsDirty();
  }

  public moveUp(item: BaseObject) {
    this.gridWrapper.moveUp(item);
    this.form.markAsDirty();
  }

  public addHandler({ sender }: any): void {
    sender.addRow(this.createFormGroupForEtape(new EtapeScenario()));
  }

  public editHandler(event: EditEvent): void {
    this.closeEditor(event.sender);
    this.gridWrapper.editedRowIndex = event.rowIndex;
    if (this.gridWrapper.editedRowIndex != null) {
      event.sender.editRow(this.gridWrapper.editedRowIndex, this.createFormGroupForEtape(event.dataItem));
    }
  }

  public removeHandler(event: RemoveEvent) {
    this.gridWrapper.removeHandler(event);
    this.form.markAsDirty();
  }

  public saveHandler(event: any): void {
    if (event.formGroup.invalid) {
      return;
    }

    this.gridWrapper.saveHandler(event);

    const etapes = this.gridWrapper.processedData;
    etapes.forEach((val, idx) => {
      val.ordre = idx;
    });
    this.form.get('etapes')?.setValue(etapes);
    this.form.markAsDirty();
  }

  private createFormGroupForEtape(dataItem: EtapeScenario): FormGroup {
    const fb = this.fb.group({
      id: [dataItem.id],
      uuid: [dataItem.uuid, Validators.required],
      nom: [dataItem.nom, Validators.required, [this.numeroPGMValidator()]],
      ordre: [dataItem.ordre],
    });

    return fb;
  }

  private closeEditor(grid: GridComponent, rowIndex = this.gridWrapper.editedRowIndex): void {
    grid.closeRow(rowIndex);
    this.gridWrapper.editedRowIndex = undefined;
  }

  private numeroPGMValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        delay(500),
        distinctUntilChanged(),
        switchMap((etapeName) => {          
          const numeroPgm = `${this.form.controls['nom'].value}-${etapeName}`;
          return this.spService.exists(numeroPgm).pipe(
            map((exists) => {
              const etape = this.originalEtapes.find((x) => x.uuid === control.parent?.value.uuid);
              const isOrginal = etape?.nom === control.value;
              return (exists || this.numeroPgmExistsInGrid(numeroPgm)) && !control.pristine && !isOrginal ? { unavailable: true } : null;
            }),
            catchError((err) => of({ error: err }))
          );
        })
      );
    };
  }

  private numeroPgmExistsInGrid(numeroPgm: string) {
    const exsists = this.gridWrapper.gridData.data.find((x) => `${this.form.controls['nom'].value}-${x.nom}` === numeroPgm);
    return exsists == null ? false : true;
  }
}
